import React, { useState, useRef, useEffect, useCallback } from 'react';

import { cn } from '../../lib/utils';
import { ROOM_TYPES } from '../../data/roomChecklists';

// ================================================================= //
//          RoomSelector — Searchable Dropdown with Free-text Entry   //
// ================================================================= //
// Provides a text input that filters the ROOM_TYPES list as the user //
// types. Supports keyboard navigation and free-text custom entries.  //
// ================================================================= //

interface RoomSelectorProps {
  /** The current room type value (e.g., "kitchen" or a custom string). */
  value: string;
  /** Called when the user selects a room type or enters custom text. */
  onChange: (value: string, label: string) => void;
  /** Optional additional CSS classes. */
  className?: string;
}

export const RoomSelector: React.FC<RoomSelectorProps> = ({
  value,
  onChange,
  className,
}) => {
  const [inputText, setInputText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Reason: Sync input text with external value changes (e.g., reset)
  useEffect(() => {
    const match = ROOM_TYPES.find((rt) => rt.value === value);
    setInputText(match ? match.label : value);
  }, [value]);

  // Reason: Contains-search (not prefix-only) for flexible matching
  const filteredTypes = ROOM_TYPES.filter((rt) =>
    rt.label.toLowerCase().includes(inputText.toLowerCase())
  );

  const hasExactMatch = ROOM_TYPES.some(
    (rt) => rt.label.toLowerCase() === inputText.toLowerCase()
  );

  const handleSelect = useCallback(
    (roomValue: string, roomLabel: string) => {
      onChange(roomValue, roomLabel);
      setInputText(roomLabel);
      setIsOpen(false);
      setHighlightIndex(-1);
    },
    [onChange]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    setIsOpen(true);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && e.key !== 'Escape') {
      setIsOpen(true);
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev < filteredTypes.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < filteredTypes.length) {
          const selected = filteredTypes[highlightIndex];
          handleSelect(selected.value, selected.label);
        } else if (inputText.trim()) {
          // Accept as free-text entry
          handleSelect(inputText.trim(), inputText.trim());
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightIndex(-1);
        break;
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Reason: Delay close to allow click on dropdown item to register
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget as Node)
    ) {
      // If text doesn't match a known type, accept as custom
      if (inputText.trim() && !hasExactMatch) {
        onChange(inputText.trim(), inputText.trim());
      }
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  };

  // Reason: Scroll highlighted item into view for long lists
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const items = listRef.current.children;
      if (items[highlightIndex]) {
        (items[highlightIndex] as HTMLElement).scrollIntoView({
          block: 'nearest',
        });
      }
    }
  }, [highlightIndex]);

  return (
    <div ref={containerRef} className={cn('relative', className)} onBlur={handleBlur}>
      <input
        ref={inputRef}
        type="text"
        value={inputText}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Search room type or enter custom..."
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        autoComplete="off"
      />

      {isOpen && (filteredTypes.length > 0 || inputText.trim()) && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-background py-1 shadow-md"
        >
          {filteredTypes.map((rt, index) => (
            <li
              key={rt.value}
              role="option"
              aria-selected={highlightIndex === index}
              className={cn(
                'cursor-pointer px-3 py-2 text-sm',
                highlightIndex === index
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent/50'
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(rt.value, rt.label);
              }}
              onMouseEnter={() => setHighlightIndex(index)}
            >
              {rt.label}
            </li>
          ))}

          {/* Show custom entry option if input doesn't exactly match */}
          {inputText.trim() && !hasExactMatch && (
            <li
              role="option"
              aria-selected={highlightIndex === filteredTypes.length}
              className={cn(
                'cursor-pointer border-t px-3 py-2 text-sm text-muted-foreground',
                highlightIndex === filteredTypes.length
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent/50'
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(inputText.trim(), inputText.trim());
              }}
            >
              Use "{inputText.trim()}" (custom)
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default RoomSelector;
